import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { sendGroupInvitation, getPendingInvitations, acceptInvitation, declineInvitation } from '../../../services/groupService';
import { useAuth } from '../../../contexts/AuthContext';

const ShareModal = ({ isOpen, onClose, tripData, groupId }) => {
  const { user } = useAuth();
  const [shareLink] = useState(`https://taralets.ph/itinerary/${tripData?.id}`);
  const [copied, setCopied] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [invitedEmails, setInvitedEmails] = useState([]);
  const [permission, setPermission] = useState('viewer');
  const [isLoading, setIsLoading] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState([]);

  useEffect(() => {
    if (isOpen && user?.id) {
      loadPendingInvitations();
    }
  }, [isOpen, user?.id]);

  const loadPendingInvitations = async () => {
    const invitations = await getPendingInvitations(user?.id);
    setPendingInvitations(invitations);
  };

  const handleCopyLink = () => {
    navigator?.clipboard?.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAddEmail = () => {
    if (emailInput && emailInput?.includes('@')) {
      setInvitedEmails(prev => [...prev, { email: emailInput, permission }]);
      setEmailInput('');
    }
  };

  const handleRemoveEmail = (email) => {
    setInvitedEmails(prev => prev?.filter(item => item?.email !== email));
  };

  const handleSendInvites = async () => {
    if (!groupId || !user?.id) {
      alert('Please create a group first before sending invitations');
      return;
    }

    setIsLoading(true);
    try {
      const results = await Promise.all(
        invitedEmails?.map(invite => 
          sendGroupInvitation(groupId, user?.id, invite?.email, invite?.permission)
        )
      );

      const successCount = results?.filter(r => r?.success)?.length;
      alert(`Successfully sent ${successCount} invitation(s)!`);
      setInvitedEmails([]);
      onClose();
    } catch (error) {
      console.error('Error sending invitations:', error);
      alert('Failed to send some invitations. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptInvitation = async (invitationId) => {
    setIsLoading(true);
    try {
      const result = await acceptInvitation(invitationId, user?.id);
      if (result?.success) {
        alert('Invitation accepted! You are now part of the group.');
        loadPendingInvitations();
      } else {
        alert('Failed to accept invitation. Please try again.');
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      alert('Failed to accept invitation.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeclineInvitation = async (invitationId) => {
    setIsLoading(true);
    try {
      const result = await declineInvitation(invitationId);
      if (result?.success) {
        alert('Invitation declined.');
        loadPendingInvitations();
      } else {
        alert('Failed to decline invitation.');
      }
    } catch (error) {
      console.error('Error declining invitation:', error);
      alert('Failed to decline invitation.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Share Itinerary</h2>
            <p className="text-sm text-muted-foreground mt-1">{tripData?.destination}</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Pending Invitations */}
          {pendingInvitations?.length > 0 && (
            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Mail" size={16} className="text-accent" />
                Pending Invitations ({pendingInvitations?.length})
              </h3>
              <div className="space-y-2">
                {pendingInvitations?.map((invitation) => (
                  <div
                    key={invitation?.id}
                    className="flex items-center justify-between p-3 bg-card rounded-lg border border-border"
                  >
                    <div>
                      <p className="text-sm font-medium text-foreground">{invitation?.groupName}</p>
                      <p className="text-xs text-muted-foreground capitalize">{invitation?.role} access</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleAcceptInvitation(invitation?.id)}
                        disabled={isLoading}
                        iconName="Check"
                      >
                        Accept
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeclineInvitation(invitation?.id)}
                        disabled={isLoading}
                        iconName="X"
                      >
                        Decline
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Share Link */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Share Link
            </label>
            <div className="flex gap-2">
              <div className="flex-1 flex items-center gap-2 px-4 py-3 bg-muted rounded-lg border border-border">
                <Icon name="Link" size={16} className="text-muted-foreground" />
                <span className="text-sm text-foreground truncate">{shareLink}</span>
              </div>
              <Button
                variant={copied ? 'success' : 'default'}
                size="default"
                onClick={handleCopyLink}
                iconName={copied ? 'Check' : 'Copy'}
                iconPosition="left"
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Anyone with this link can view your itinerary
            </p>
          </div>

          {/* Invite by Email */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Invite Collaborators
            </label>
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e?.target?.value)}
                  placeholder="Enter email address"
                  className="flex-1"
                />
                <select
                  value={permission}
                  onChange={(e) => setPermission(e?.target?.value)}
                  className="px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="viewer">Can View</option>
                  <option value="editor">Can Edit</option>
                </select>
                <Button
                  variant="default"
                  size="default"
                  onClick={handleAddEmail}
                  iconName="Plus"
                />
              </div>

              {/* Invited Emails List */}
              {invitedEmails?.length > 0 && (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {invitedEmails?.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                          <Icon name="User" size={14} className="text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{item?.email}</p>
                          <p className="text-xs text-muted-foreground capitalize">{item?.permission} access</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveEmail(item?.email)}
                        className="w-8 h-8 rounded-lg hover:bg-destructive/10 flex items-center justify-center transition-colors"
                      >
                        <Icon name="X" size={14} className="text-destructive" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Social Share */}
          <div>
            <label className="text-sm font-medium text-foreground mb-3 block">
              Share on Social Media
            </label>
            <div className="grid grid-cols-4 gap-3">
              <button className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:bg-muted transition-colors">
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                  <Icon name="Facebook" size={20} className="text-white" />
                </div>
                <span className="text-xs text-foreground">Facebook</span>
              </button>
              <button className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:bg-muted transition-colors">
                <div className="w-10 h-10 rounded-full bg-blue-400 flex items-center justify-center">
                  <Icon name="Twitter" size={20} className="text-white" />
                </div>
                <span className="text-xs text-foreground">Twitter</span>
              </button>
              <button className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:bg-muted transition-colors">
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                  <Icon name="MessageCircle" size={20} className="text-white" />
                </div>
                <span className="text-xs text-foreground">WhatsApp</span>
              </button>
              <button className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:bg-muted transition-colors">
                <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center">
                  <Icon name="Mail" size={20} className="text-white" />
                </div>
                <span className="text-xs text-foreground">Email</span>
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              size="lg"
              fullWidth
              onClick={onClose}
            >
              Close
            </Button>
            {invitedEmails?.length > 0 && (
              <Button
                type="button"
                variant="default"
                size="lg"
                fullWidth
                onClick={handleSendInvites}
                iconName="Send"
                iconPosition="left"
                loading={isLoading}
                disabled={isLoading}
              >
                Send Invites ({invitedEmails?.length})
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;